// @ts-nocheck

import { createSelector } from 'reselect';

import { type StateType } from '../../../states';
import { MPCardStatuses } from '../types/cards';
import { type MPFamilyId } from '../types/families';
import { MPNotificationsTypes, MPUserStatuses } from '../types/user';

const emptyObj = {};
const emptyArray: [] = [];

const getCardIndexFromProps = (_: StateType, { index }: { index: number }) => index;
const getFamilyIdFromProps = (_: StateType, { familyId }: { familyId: MPFamilyId }) => familyId;

export const selectMPUserState = createSelector(
  (state: StateType) => state?.mp?.user ?? emptyObj,
  (userState) => userState,
);

export const selectMPUserStatus = createSelector(selectMPUserState, (userState) => userState.status);

export const selectMPUserProgress = createSelector(selectMPUserState, (userState) => userState.progress ?? emptyObj);

export const selectMPUserNotifications = createSelector(
  selectMPUserState,
  (userState) => userState.notifications ?? emptyArray,
);

export const selectMPUserProgressOpen = createSelector(selectMPUserProgress, (userProgress) =>
  Object.keys(userProgress).filter((familyId) =>
    Object.keys(userProgress[familyId].cards).find(
      (key) => userProgress[familyId].cards[key].status === MPCardStatuses.OPEN,
    ),
  ),
);

export const selectMPUserShouldBeAskToGetRegistered = createSelector(
  selectMPUserStatus,
  (userStatus) => userStatus !== MPUserStatuses.IN && userStatus !== MPUserStatuses.EXCLUDED,
);

export const selectMPUserFamily = createSelector(
  [selectMPUserProgress, getFamilyIdFromProps],
  (userProgress, familyId) => userProgress[familyId],
);

export const selectHasMPUserCompletedFamily = createSelector(selectMPUserFamily, (family) => {
  return family?.status === MPCardStatuses.WON;
});

export const selectMPUserFamilyWonAmount = createSelector(selectMPUserFamily, (family) => family?.wonAmount || 0);

export const selectMPUserCard = createSelector([selectMPUserFamily, getCardIndexFromProps], (family, index) => {
  if (!family) {
    return;
  }

  return family.cards[index + 1];
});

export const selectMPUserCardStatus = createSelector([selectMPUserCard], (card) => card?.status || MPCardStatuses.OPEN);

export const selectMPTotalNbCards = createSelector(selectMPUserProgress, (userProgress) =>
  Object.keys(userProgress).reduce((acc, familyId) => acc + Object.keys(userProgress[familyId].cards).length, 0),
);

export const selectMPUserNbCardsWon = createSelector(selectMPUserProgress, (userProgress) =>
  Object.keys(userProgress).reduce(
    (acc, familyId) =>
      acc +
      Object.keys(userProgress[familyId].cards).reduce(
        (_acc, cardId) => (userProgress[familyId].cards[cardId].status === MPCardStatuses.WON ? _acc + 1 : _acc),
        0,
      ),
    0,
  ),
);

export const selectMPUserCardsBetslipRefs = createSelector(selectMPUserFamily, (family) =>
  Object.keys(family.cards).map((cardId) => family.cards[cardId]?.bet?.betslipReference ?? null),
);

export const selectHasMPUserAnyFamilyCompletedNotification = createSelector(
  selectMPUserNotifications,
  (notifications) => notifications.some((notification) => notification.type === MPNotificationsTypes.FAMILY_COMPLETED),
);

export const selectMPNotificationUUIDS = createSelector(selectMPUserNotifications, (notifications) =>
  notifications.reduce(
    (acc, notification) =>
      notification.type === MPNotificationsTypes.FAMILY_COMPLETED ? [...acc, notification.uuid] : acc,
    [],
  ),
);

export const selectIsMPUserOptIn = createSelector(selectMPUserStatus, (userStatus) => userStatus === MPUserStatuses.IN);
